// vendors
import { combineLatest, Observable } from 'rxjs';
import { combineLatestWith, filter, map } from 'rxjs/operators';
// libs
import { CellExportFacade, NotebookTypes } from '@mode/notebooks/contract';
// legacy
import { NotebookResource } from '@mode/legacy/data-access-model-js';
import { NotebookState } from '@mode/legacy/types-app-state';
import { NotebookSelectors } from '~/selectors/notebook.selectors';
import { AppStore } from '~/types/main.types';

import { Actions } from '../../../actions';

export class NotebookStateService {
  static $inject = ['RxStoreService', 'actions$', 'CellExportFacade'];

  selectedNotebookToken$: Observable<string>;
  notebookState$: Observable<NotebookState>;
  notebookResource$: Observable<NotebookResource>;

  canRestartSession$: Observable<boolean>;
  notebookType$: Observable<NotebookTypes.NotebookType>;

  cellExecuting$: Observable<boolean>;
  contentType$: Observable<NotebookTypes.ContentType>;
  isInLayout$: Observable<boolean>;
  isEditing$: Observable<boolean>;
  isFrozen$: Observable<boolean>;
  isFolded$: Observable<boolean>;
  cellToken$: Observable<string>;
  nextCellToken$: Observable<string>;
  prevCellToken$: Observable<string>;

  canFreeze$: Observable<boolean>;
  canDelete$: Observable<boolean>;

  isSelectedCellExported$: Observable<boolean>;
  isExportInProgress$: Observable<boolean>;

  hasResult$: Observable<boolean>;

  constructor(
    private rxStore: AppStore,
    private actions$: Observable<Actions>,
    private cellExportFacade: CellExportFacade
  ) {
    this.selectedNotebookToken$ = this.rxStore.select(NotebookSelectors.selectedNotebookToken);
    this.notebookState$ = this.rxStore.selectWithParam(
      NotebookSelectors.statusBarStateByNotebook,
      this.selectedNotebookToken$
    );
    this.notebookResource$ = this.rxStore
      .select(NotebookSelectors.selectedNotebookResource)
      .pipe(filter((r) => r !== null));
    this.notebookType$ = this.rxStore.select(NotebookSelectors.selectedNotebookType);

    this.cellToken$ = this.rxStore.select(NotebookSelectors.selectedCellToken);
    this.cellExecuting$ = this.rxStore.select(NotebookSelectors.selectedCellExecuting);
    this.contentType$ = this.rxStore.select(NotebookSelectors.selectedCellContentType);
    this.nextCellToken$ = this.rxStore.select(NotebookSelectors.selectedNextCellToken);
    this.prevCellToken$ = this.rxStore.select(NotebookSelectors.selectedPrevCellToken);

    this.isEditing$ = this.rxStore.select(NotebookSelectors.isEditing);
    this.isFrozen$ = this.rxStore.select(NotebookSelectors.selectedCellFrozen);
    this.isFolded$ = this.rxStore.select(NotebookSelectors.selectedCellFolded);
    this.isInLayout$ = this.rxStore.select(NotebookSelectors.selectedCellIsInLayout);

    this.canRestartSession$ = this.notebookState$.pipe(map((s) => s !== NotebookState.Session));
    this.canFreeze$ = this.rxStore.selectWithParam(NotebookSelectors.canFreezeByCell, this.cellToken$);
    this.canDelete$ = combineLatest([
      this.cellToken$,
      this.cellExecuting$,
      this.cellExportFacade.exportingCellToken$,
    ]).pipe(
      map(([token, executing, exportingCellToken]) => {
        return token !== null && !executing && exportingCellToken !== token;
      })
    );
    this.hasResult$ = this.rxStore.select(NotebookSelectors.selectedCellHasResult);

    this.isSelectedCellExported$ = this.rxStore.selectWithParam(NotebookSelectors.isExportedByCell, this.cellToken$);
    this.isExportInProgress$ = this.cellExportFacade.isExporting$;
  }

  canRunCell$ = (notebookToken) => this.rxStore.select(NotebookSelectors.createCanCellRunSelector(notebookToken));
}
